/* eslint-disable max-lines -- the singleton-MessageActions wiring
 * (flag-gated by `ano-singleton-message-actions`) adds ~80 LOC of
 * provider hookup, payload construction, and right-click-menu fork
 * to this file. Will be removed once the singleton path is the only
 * path and the flag is deleted; until then, contained here so the
 * legacy path stays a single straightforward diff away. */
import React, {
  useState,
  useMemo,
  useRef,
  useCallback,
  useEffect,
} from "react";
import { useQuery, useZero } from "@rocicorp/zero/react";
import { queries } from "@/lib/queries";
import { useWorkspacePublicState } from "@/lib/selectors/use-is-public-workspace";
import { useCurrentUser } from "@/lib/current-user";
import { usePostMountFrame } from "@/lib/use-post-mount-frame";
import { scrollRowHydrated } from "@/lib/scroll-profile";
import { SINGLETON_MESSAGE_ACTIONS } from "@/lib/debug-flags";
import { MessageActions } from "./MessageActions";
import { DeleteMessageDialog } from "./DeleteMessageDialog";
import {
  type MessageActionHandlers,
  useHoveredMessageApi,
} from "./use-hovered-message";
import { cn } from "@/lib/utils";
import { AnimatedImage } from "@/components/ui/animated-image";
import { mutators } from "@/lib/mutators";
import { type TiptapEditorRef } from "./TiptapEditor";
import { extractMentionIds } from "@/lib/mentions";
import { isSystemLike, isThreadable, type MessageKind } from "@ano/shared";
import { resolveUserDisplayName } from "@/lib/user-display-name";
import { ContextMenu, ContextMenuTrigger } from "@/components/ui/context-menu";
import { toast } from "sonner";
import { appOrigin, buildPublicQuestionPath } from "@/lib/app-route";
import { generateSlugFromName } from "@/lib/workspace-validation";

import { SystemMessageRow } from "./message/SystemMessageRow";
import { MessageHeader } from "./message/MessageHeader";
import { MessageBodyArea } from "./message/MessageBodyArea";
import { MessageReactions } from "./message/MessageReactions";
import {
  MessageCommunitySignals,
  type SignalType,
} from "./message/MessageCommunitySignals";
import { ThreadReplyPreview } from "./message/ThreadReplyPreview";
import { MessageContextMenu } from "./message/MessageContextMenu";
import { AgentIndicator } from "./message/AgentIndicator";
import {
  buildDisplayNameMap,
  buildMentionableUsers,
  groupReactionsByEmoji,
  type ReactionGroup,
} from "./message/message-derivations";
import type { MessageProps } from "./message/message-props";

export const Message = React.memo(function Message({
  message,
  user: userProp,
  reactions: reactionList = [],
  signals: signalList = [],
  linkPreviews: linkPreviewList = [],
  attachments: attachmentList = [],
  userMap,
  workspaceMembers,
  compact,
  onThreadClick,
  onInlineReply,
  onCreateThread,
  onScrollToMessage,
  quotedSource,
  quotedSourceAuthorName,
  quotedSourceAuthorAvatarURL,
  threadReplyCount,
  threadReplyUsers,
  threadLatestReplyAt,
  threadActive = false,
  highlighted,
  isPinned,
  onPin,
  onUserClick,
  actionPillOpen,
  onActionPillOpenChange,
  onJoinCall,
  onSendToShell,
  channelId,
  channels,
  onChannelClick,
  senderIsFormerMember = false,
}: MessageProps) {
  const { user: currentUser } = useCurrentUser();
  const zero = useZero();
  const user = userProp ?? null;

  // Community signals are PUBLIC-community-only (the server mutator rejects them
  // in private workspaces, #1931). Gate the affordance so it never shows where
  // the write would bounce — mirrors ThreadPanel's Q&A-controls gate. The
  // channel is already subscribed (Zero dedups) and the workspace-list read
  // opens no new sub.
  const [signalChannel] = useQuery(
    channelId ? queries.channels.byId(channelId) : null,
  );
  const { isPublic: signalsEnabled } = useWorkspacePublicState(
    signalChannel?.workspace_id,
  );

  // Public-link affordance: only top-level messages in a PUBLIC, non-archived
  // channel of a PUBLIC community workspace get a shareable `…/c/<channel>/m/<id>`
  // deep link. A message in a PRIVATE channel of a public workspace is NOT
  // publicly readable (the endpoint 404s), so the affordance must not be offered
  // for it. Gating on public-workspace + public-non-archived-channel + top-level
  // (rather than reading `anonymous_view`, which isn't in the client workspace
  // row) is conservative enough. Zero dedups the workspace-list sub with
  // `useWorkspacePublicState`, so no extra sub.
  const channelPubliclyShareable =
    signalsEnabled &&
    !message.thread_id &&
    signalChannel?.is_private === false &&
    !signalChannel?.is_archived;
  const [workspacesForSlug] = useQuery(
    channelPubliclyShareable ? queries.workspaces.byUser() : null,
  );
  const publicLinkSlug = useMemo(() => {
    if (!channelPubliclyShareable || !signalChannel?.workspace_id) {
      return null;
    }
    const ws = (workspacesForSlug ?? []).find(
      (w) => w.id === signalChannel.workspace_id,
    );
    if (!ws) return null;
    return ws.slug || generateSlugFromName(ws.name ?? "");
  }, [
    channelPubliclyShareable,
    signalChannel?.workspace_id,
    workspacesForSlug,
  ]);

  const displayNameMap = useMemo(() => buildDisplayNameMap(userMap), [userMap]);
  const mentionableUsers = useMemo(
    () => buildMentionableUsers(workspaceMembers, userMap),
    [workspaceMembers, userMap],
  );

  const [isEditing, setIsEditing] = useState(false);
  const editEditorRef = useRef<TiptapEditorRef>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [internalShowActions, setInternalShowActions] = useState(false);
  const [actionsLocked, setActionsLocked] = useState(false);
  const showActions = actionPillOpen ?? internalShowActions;
  const setShowActions = (next: boolean | ((prev: boolean) => boolean)) => {
    const resolved = typeof next === "function" ? next(showActions) : next;
    if (actionPillOpen === undefined) {
      setInternalShowActions(resolved);
    }
    onActionPillOpenChange?.(resolved);
  };

  const displayName = resolveUserDisplayName(user);
  const initials = displayName.slice(0, 2).toUpperCase();
  const time = new Date(message.created_at).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const isAgentAuthored = message.metadata?.agent === true;
  const isCoworkerAuthored = message.metadata?.coworker === true;
  // "Sent via the ano CLI by a human" — i.e. metadata.agent=true (set by
  // the MCP HTTP send path) but the author isn't a coworker and the
  // message isn't an automation-engine write (no apps_used denorm).
  // Header renders a "VIA CLI" pill so the chat pane distinguishes
  // CLI-sourced messages from desktop-sent messages without dragging
  // them into the AutomationMessageCard treatment.
  const viaCli =
    isAgentAuthored &&
    !isCoworkerAuthored &&
    user?.is_coworker !== true &&
    (message.metadata?.apps_used?.length ?? 0) === 0;
  const isOwn = currentUser?.id === message.user_id;

  // Discord-shape pill gating: hide the per-message Reply / Create
  // Thread affordances on non-threadable kinds + rows inside a thread.
  const canStartConversation =
    isThreadable(message.kind as MessageKind) && !message.thread_id;
  // `setShowActions` is intentionally omitted from deps — it's a fresh
  // wrapper each render, but only reads parent callbacks; including it
  // would re-create these closures and bust future MessageActions memo.
  /* eslint-disable react-hooks/exhaustive-deps */
  const handleInlineReplyClick = useCallback(() => {
    onInlineReply?.(message.id);
    setShowActions(false);
  }, [onInlineReply, message.id]);
  const handleCreateThreadClick = useCallback(() => {
    onCreateThread?.(message.id);
    setShowActions(false);
  }, [onCreateThread, message.id]);
  /* eslint-enable react-hooks/exhaustive-deps */

  // `apps_used` populated only when the message was inserted by the
  // automation engine (`automation-engine-apps-denorm.ts`); chat-driven
  // agent messages omit it. Forwarded to `AutomationMessageCard`, which
  // renders the app icons in its own header strip.
  const automationApps = message.metadata?.apps_used ?? [];
  const agentIndicator = (
    <AgentIndicator isCoworkerAuthored={isCoworkerAuthored} />
  );

  const handleSaveEdit = useCallback(() => {
    const md = editEditorRef.current?.getMarkdown()?.trim();
    if (!md) return;
    const mentionIds = extractMentionIds(md, workspaceMembers || []);
    zero.mutate(
      mutators.messages.update({
        id: message.id,
        content: md,
        is_edited: true,
        ...(mentionIds.length > 0 ? { mentions: mentionIds } : {}),
        updated_at: Date.now(),
      }),
    );
    setIsEditing(false);
  }, [message.id, workspaceMembers, zero]);

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
  }, []);

  const handleDelete = async () => {
    zero.mutate(mutators.messages.delete({ id: message.id }));
  };

  // Dedupe rapid double-clicks of the same emoji. Migration 164 added a
  // UNIQUE(message_id, user_id, emoji) index to the reactions table; a second
  // click fired before the first optimistic write settles would read a stale
  // `reactionList`, miss the in-flight row, and fire a duplicate insert that
  // violates UNIQUE on the server push — rolling back the optimistic reaction.
  // Mirrors the `signalInFlight` guard on `handleSignal` below.
  const reactionInFlight = useRef<Set<string>>(new Set());
  const handleReact = (emoji: string) => {
    if (!currentUser) return;
    if (reactionInFlight.current.has(emoji)) return;
    const existing = reactionList.find(
      (r) => r.user_id === currentUser.id && r.emoji === emoji,
    );
    reactionInFlight.current.add(emoji);
    // Clear once the optimistic local apply settles — `reactionList` then
    // reflects the toggle, so the stale-read window is closed.
    const settle = () => reactionInFlight.current.delete(emoji);
    const op = existing
      ? mutators.reactions.delete({ id: existing.id })
      : mutators.reactions.insert({
          id: crypto.randomUUID(),
          message_id: message.id,
          user_id: currentUser.id,
          emoji,
          created_at: Date.now(),
        });
    void zero.mutate(op).client.then(settle, settle);
  };

  // Toggle a generic community-curation signal for the current viewer.
  // Best-answer marks come from the thread Q&A acceptance controls.
  // Mirrors handleReact: the mutator derives channel + workspace from the
  // message and enforces active membership server-side (Pattern 6/10).
  const signalInFlight = useRef<Set<SignalType>>(new Set());
  const handleSignal = (type: SignalType) => {
    if (!currentUser) return;
    // Dedupe rapid double-clicks: a second toggle of the same type while the
    // first optimistic write is still in flight would read a stale
    // `signalList` and double-insert, hitting the
    // (message_id, user_id, signal_type) unique index.
    if (signalInFlight.current.has(type)) return;
    const existing = signalList.find(
      (s) => s.user_id === currentUser.id && s.signal_type === type,
    );
    signalInFlight.current.add(type);
    const settle = () => signalInFlight.current.delete(type);
    const op = existing
      ? mutators.community_signals.delete({ id: existing.id })
      : mutators.community_signals.insert({
          id: crypto.randomUUID(),
          message_id: message.id,
          signal_type: type,
          created_at: Date.now(),
        });
    // Clear once the optimistic local apply settles — `signalList` then
    // reflects the toggle, so the stale-read window is closed.
    void zero.mutate(op).client.then(settle, settle);
  };

  // Stable display order for emojis. The raw `groupReactionsByEmoji` Map
  // inserts in encounter order — so when a viewer removes their reaction
  // (often the earliest row for that emoji), the next reactor's timestamp
  // becomes the new "first occurrence" and the emoji shifts position.
  // `emojiOrderRef` remembers the slot an emoji took the first time it
  // appeared on this message instance; subsequent renders sort by it.
  const emojiOrderRef = useRef<Map<string, number>>(new Map());
  const reactionGroups = useMemo(() => {
    const raw = groupReactionsByEmoji(
      reactionList,
      displayNameMap,
      currentUser?.id,
    );
    const order = emojiOrderRef.current;
    let next = order.size;
    for (const emoji of raw.keys()) {
      if (!order.has(emoji)) order.set(emoji, next++);
    }
    const sorted = new Map<string, ReactionGroup>(
      Array.from(raw.entries()).sort(
        (a, b) => (order.get(a[0]) ?? 0) - (order.get(b[0]) ?? 0),
      ),
    );
    return sorted;
  }, [reactionList, displayNameMap, currentUser?.id]);

  // Two-stage row hydration: phase 1 paints the row shell (avatar +
  // header + body + reactions + thread-reply preview); phase 2 (one
  // rAF later) hydrates the hover-only action pill — `MessageActions`
  // is `position: absolute` so it doesn't affect layout, making it
  // safe to defer. Anything that contributes to scrollHeight stays
  // in phase 1 — deferring it would grow scrollHeight after the
  // synchronous channel-switch pin and create a one-frame
  // bottom-rows-pop-in on every channel switch.
  const hydrated = usePostMountFrame();
  useEffect(() => {
    if (hydrated) scrollRowHydrated();
  }, [hydrated]);

  // ─── Singleton MessageActions wiring ──────────────────────────────
  // When `SINGLETON_MESSAGE_ACTIONS` is on AND a provider is mounted
  // above us, MessageActions is rendered ONCE per channel by
  // `<HoveredMessageActions />` (positioned over whichever row is
  // hovered). The row reports its hover state + a handler getter to
  // the singleton instead of mounting its own pill — saves ~7 Radix
  // Tooltip mounts per row, which is the dominant phase-2 hitch on
  // shift-up.
  //
  // We always call the hook (rule-of-hooks); the flag + null context
  // check decides whether to actually use the singleton path.
  //
  // Use the API-only hook: Message subscribes to nothing reactive
  // (hovered / locked are not read during render here). Subscribing
  // the reactive context would re-render every Message in the slice
  // on each hover transition — ~200 rows × per-row Zero selectors +
  // closure churn = visible cursor lag when scrolled up in history.
  const hoverCtx = useHoveredMessageApi();
  const singletonEnabled = SINGLETON_MESSAGE_ACTIONS && hoverCtx !== null;
  const rowOuterRef = useRef<HTMLDivElement>(null);
  // Latest handler bundle, mutated each render. `getHandlers` returns
  // it on demand so the singleton fires the freshest closure — relevant
  // when row props like `isPinned` change between mouseenter and click.
  const handlersRef = useRef<MessageActionHandlers>({});
  const getHandlers = useCallback(() => handlersRef.current, []);

  // System message rendering. Dispatches on `kind` — predicates replaced the
  // legacy is_system_message branch and content-string matching; Phase 4a
  // dropped the column.
  const kind = ((message.kind as MessageKind | null | undefined) ??
    "text") as MessageKind;
  if (isSystemLike(kind)) {
    return (
      <SystemMessageRow
        messageId={message.id}
        kind={kind}
        content={message.content}
        displayName={displayName}
        time={time}
        onJoinCall={onJoinCall}
        payload={
          (message.payload as Record<string, unknown> | null | undefined) ??
          null
        }
        userMap={userMap}
        onThreadClick={onThreadClick}
      />
    );
  }

  const handleCopyText = () => {
    navigator.clipboard
      .writeText(message.content)
      .then(() => toast.success("Message copied"))
      .catch(() => toast.error("Couldn't copy"));
  };

  const handleCopyLink = () => {
    const params = new URLSearchParams();
    if (channelId) params.set("channel", channelId);
    if (message.thread_id) params.set("thread", message.thread_id);
    params.set("message", message.id);
    const link = `${window.location.origin}/?${params.toString()}`;
    navigator.clipboard
      .writeText(link)
      .then(() => toast.success("Link copied"))
      .catch(() => toast.error("Couldn't copy link"));
  };

  // Shareable PUBLIC deep link (for X / Reddit unfurls). Built against the
  // canonical shareable app origin — NEVER `window.location.origin`, which can
  // be `ano-app://localhost` (Electron) or a localhost dev host.
  const handleCopyPublicLink = () => {
    const channelName = signalChannel?.name;
    if (!publicLinkSlug || !channelName) return;
    const link = `${appOrigin(window.location, { shareable: true })}${buildPublicQuestionPath(
      publicLinkSlug,
      channelName,
      message.id,
    )}`;
    navigator.clipboard
      .writeText(link)
      .then(() => toast.success("Public link copied"))
      .catch(() => toast.error("Couldn't copy link"));
  };

  const handleEditFromMenu = () => {
    setIsEditing(true);
    requestAnimationFrame(() => {
      editEditorRef.current?.setContentFromMarkdown(message.content);
      editEditorRef.current?.focus();
    });
  };

  // Build the per-row action bundle. The same shape is consumed by
  // the inline `<MessageActions />` (legacy path) and by the singleton
  // via `handlersRef`. Each callback calls `setShowActions(false)`
  // (no-op under singleton) so the legacy path's pill dismisses on
  // action; the singleton's own wrapper also clears the hover after
  // invoking, so both paths converge on "action fires, pill goes
  // away".
  const actionHandlers: MessageActionHandlers = {
    onEdit: () => {
      setIsEditing(true);
      setShowActions(false);
      requestAnimationFrame(() => {
        editEditorRef.current?.setContentFromMarkdown(message.content);
        editEditorRef.current?.focus();
      });
    },
    onDelete: () => {
      setShowDeleteDialog(true);
      setShowActions(false);
    },
    onReact: (emoji) => {
      handleReact(emoji);
      setShowActions(false);
    },
    onInlineReply:
      canStartConversation && onInlineReply
        ? handleInlineReplyClick
        : undefined,
    onCreateThread:
      canStartConversation && onCreateThread
        ? handleCreateThreadClick
        : undefined,
    onPin: onPin
      ? () => {
          onPin();
          setShowActions(false);
        }
      : undefined,
    onSendToShell: onSendToShell
      ? () => {
          onSendToShell(message.id);
          setShowActions(false);
        }
      : undefined,
  };
  handlersRef.current = actionHandlers;

  // Push hover payload to the singleton context. Stable identity for
  // the row element + ref; only re-fires when the row is actually
  // entered. `clearHovered` is debounced so the row→pill mouse-gap
  // doesn't flicker.
  const handleRowMouseEnter = () => {
    if (singletonEnabled && hoverCtx && rowOuterRef.current) {
      hoverCtx.setHovered({
        messageId: message.id,
        element: rowOuterRef.current,
        getHandlers,
        isOwn,
        isPinned: !!isPinned,
      });
    } else {
      setShowActions(true);
    }
  };
  const handleRowMouseLeave = () => {
    if (singletonEnabled && hoverCtx) {
      hoverCtx.clearHovered();
    } else if (!actionsLocked) {
      setShowActions(false);
    }
  };

  return (
    <>
      <ContextMenu>
        <ContextMenuTrigger asChild disabled={isEditing}>
          <div
            ref={rowOuterRef}
            data-message-id={message.id}
            className={cn(
              "group",
              // Under singleton, `actions-open` is imperatively added
              // to this element by `<HoveredMessageActions />`. Skip
              // the local class so re-renders triggered by other
              // sources don't toggle it off mid-hover.
              !singletonEnabled && showActions && "actions-open",
            )}
            onMouseEnter={handleRowMouseEnter}
            onMouseLeave={handleRowMouseLeave}
          >
            <div
              className={cn(
                "relative flex gap-3 p-2 rounded-lg hover:text-foreground hover:bg-foreground/[0.025] transition-colors",
                // Mirror the hover styles whenever the row is marked
                // `actions-open` — i.e. its action pill is currently
                // showing. Without this, hovering the singleton pill
                // (portal'd to body, outside the row's DOM subtree)
                // drops the row's `:hover` pseudo-class and the user
                // loses the "which message am I about to act on" cue.
                // Selector reads: "when an ancestor has .actions-open,
                // apply this style to me."
                "[.actions-open_&]:bg-foreground/[0.025] [.actions-open_&]:text-foreground",
                highlighted && "bg-info-muted ring-1 ring-info/40",
              )}
              // Split durations: instant 0ms for hover (the steady
              // state — feels native), slow 500ms only while the
              // scroll-to-message highlight is active so it reads as
              // a deliberate flash rather than a flicker. Inline
              // style is render-safe — React's DOM patcher diffs
              // style by value, the object literal recomputes only
              // when Message re-renders, no child memo deps touched.
              style={{ transitionDuration: highlighted ? "500ms" : "0ms" }}
            >
              {compact ? (
                <div className="w-9 shrink-0 flex items-start justify-center">
                  {!isAgentAuthored && (
                    <span className="text-[10px] text-muted-foreground/50 opacity-0 group-hover:opacity-100">
                      {time}
                    </span>
                  )}
                </div>
              ) : (
                <div className="h-9 w-9 rounded-full bg-foreground/20 flex items-center justify-center text-xs font-semibold shrink-0 overflow-hidden">
                  {user?.avatar_url ? (
                    <AnimatedImage
                      src={user.avatar_url}
                      alt={displayName}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    initials
                  )}
                </div>
              )}
              <div className="flex-1 min-w-0">
                {compact && isAgentAuthored && (
                  <div className="mb-1 flex items-center gap-1.5">
                    <span className="text-[10px] text-muted-foreground/60">
                      {time}
                    </span>
                    {agentIndicator}
                  </div>
                )}
                {!compact && (
                  <MessageHeader
                    userId={message.user_id}
                    displayName={displayName}
                    user={user}
                    isCoworkerAuthored={isCoworkerAuthored}
                    viaCli={viaCli}
                    senderIsFormerMember={senderIsFormerMember}
                    time={time}
                    agentIndicator={agentIndicator}
                    isPinned={isPinned}
                    isEdited={message.is_edited}
                    onUserClick={onUserClick}
                  />
                )}
                <MessageBodyArea
                  isEditing={isEditing}
                  editEditorRef={editEditorRef}
                  onSave={handleSaveEdit}
                  onCancel={handleCancelEdit}
                  isAgentAuthored={isAgentAuthored}
                  isCoworkerAuthored={isCoworkerAuthored}
                  automationApps={automationApps}
                  message={message}
                  quotedSource={quotedSource}
                  quotedSourceAuthorName={quotedSourceAuthorName}
                  quotedSourceAuthorAvatarURL={quotedSourceAuthorAvatarURL}
                  isOwn={isOwn}
                  mentionableUsers={mentionableUsers}
                  onUserClick={onUserClick}
                  channels={channels}
                  onChannelClick={onChannelClick}
                  attachmentList={attachmentList}
                  linkPreviewList={linkPreviewList}
                  onThreadClick={onThreadClick}
                  onScrollToMessage={onScrollToMessage}
                />

                <MessageReactions
                  groups={reactionGroups}
                  onReact={handleReact}
                />

                <MessageCommunitySignals
                  signals={signalList}
                  currentUserId={currentUser?.id}
                  onSignal={handleSignal}
                />

                <ThreadReplyPreview
                  messageId={message.id}
                  threadReplyCount={threadReplyCount}
                  threadReplyUsers={threadReplyUsers}
                  threadLatestReplyAt={threadLatestReplyAt}
                  threadActive={threadActive}
                  userMap={userMap}
                  onThreadClick={onThreadClick}
                />
              </div>
              {!singletonEnabled && hydrated && (
                <MessageActions
                  open={showActions}
                  onLockOpen={setActionsLocked}
                  onEdit={actionHandlers.onEdit}
                  onDelete={actionHandlers.onDelete}
                  onReact={actionHandlers.onReact}
                  // Discord-shape "Reply" + "Create Thread" — uniform on
                  // every threadable message. Reply's mutator branch picks
                  // inline vs thread mode by `parent.thread_id`. Both
                  // hidden on non-threadable kinds (system / call) and on
                  // rows already inside a thread (no nested threads;
                  // those use the sidebar composer instead).
                  onInlineReply={actionHandlers.onInlineReply}
                  onCreateThread={actionHandlers.onCreateThread}
                  onPin={actionHandlers.onPin}
                  isOwn={isOwn}
                  isPinned={isPinned}
                  onSendToShell={actionHandlers.onSendToShell}
                />
              )}
            </div>
          </div>
        </ContextMenuTrigger>
        <MessageContextMenu
          messageId={message.id}
          messageKind={message.kind}
          isOwn={isOwn}
          isPinned={isPinned}
          onInlineReply={
            canStartConversation && onInlineReply
              ? handleInlineReplyClick
              : undefined
          }
          onCreateThread={
            canStartConversation && onCreateThread
              ? handleCreateThreadClick
              : undefined
          }
          onAddReaction={() => {
            if (singletonEnabled && hoverCtx && rowOuterRef.current) {
              // Position the singleton pill over this row + lock it
              // open. User picks an emoji from the visible quick row
              // or clicks "more reactions" to expand the picker.
              hoverCtx.setHovered({
                messageId: message.id,
                element: rowOuterRef.current,
                getHandlers,
                isOwn,
                isPinned: !!isPinned,
              });
              hoverCtx.setLocked(true);
            } else {
              setShowActions(true);
              setActionsLocked(true);
            }
          }}
          onSignal={signalsEnabled ? handleSignal : undefined}
          onCopyText={handleCopyText}
          onCopyLink={handleCopyLink}
          onCopyPublicLink={
            publicLinkSlug && signalChannel?.name
              ? handleCopyPublicLink
              : undefined
          }
          onPin={onPin}
          onSendToShell={onSendToShell}
          onEdit={handleEditFromMenu}
          onDelete={() => setShowDeleteDialog(true)}
        />
      </ContextMenu>
      <DeleteMessageDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        onConfirm={handleDelete}
      />
    </>
  );
});
